/**
 * Title, pause, settings and death screens.
 *
 * All four are built once at boot and reused. They are the only parts of the UI
 * that take pointer events, and the only parts that ever cover the world.
 */

import { SAVE_KEY } from '../core/config';
import { hashSeed } from '../core/rng';
import type { ResumeOffer } from '../core/systems';
import type { GameSettings, RenderSettings, SaveData } from '../core/types';
import { add, button, el, fmtTime } from './dom';
import type { Dispatch, Screen } from './screen';

type Progress = SaveData['progress'];

const FLAVOUR: readonly string[] = [
  'You noclipped out of reality.',
  'Six hundred million square miles of damp carpet.',
  'The hum is not coming from the lights.',
  'Nothing in here has ever been outside.',
  'If it sounds close, it already knows.',
  'Every room is the room you were just in.',
];

/** Deep enough for `GameSettings`; only `render` is nested. */
export function cloneSettings(s: GameSettings): GameSettings {
  return { ...s, render: { ...s.render } };
}

/**
 * The title screen reads the save blob directly rather than importing the save
 * system: subsystems do not import each other, and `UISystem` has no hook for
 * progress. Any failure just shows an empty record.
 */
function readProgress(): Progress {
  const blank: Progress = { deepestLevel: 0, bestTime: 0, runs: 0, deaths: 0, notesFound: [] };
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    if (!raw) return blank;
    const parsed = JSON.parse(raw) as Partial<SaveData> | null;
    const p = parsed?.progress;
    if (!p) return blank;
    return {
      deepestLevel: Number(p.deepestLevel) || 0,
      bestTime: Number(p.bestTime) || 0,
      runs: Number(p.runs) || 0,
      deaths: Number(p.deaths) || 0,
      notesFound: Array.isArray(p.notesFound) ? p.notesFound : [],
    };
  } catch {
    return blank;
  }
}

/* ------------------------------------------------------------------ *
 * Title
 * ------------------------------------------------------------------ */

export class TitleScreen implements Screen {
  readonly el = el('section', 'ui-screen ui-title');

  private readonly flavour = el('p', 'ui-title-flavour');
  private readonly record = el('div', 'ui-record');
  private readonly seed: HTMLInputElement;
  private readonly startBtn: HTMLButtonElement;

  /**
   * The offer to go back, above ENTER.
   *
   * A player who closed the tab by accident should not have to wonder whether
   * the building kept their run, so when there is a snapshot the title leads
   * with CONTINUE and says where it would put them and when they left it.
   * ENTER goes on meaning exactly what it always did: a new maze.
   */
  private readonly continueWrap = el('div', 'ui-title-continue');
  private readonly continueBtn: HTMLButtonElement;
  private readonly continueLine = el('p', 'ui-menu-sub ui-title-continue-line');
  private resumable = false;

  constructor(dispatch: Dispatch, openSettings: () => void) {
    const name = el('h1', 'ui-title-name');
    // Split so the second word can drift independently of the first.
    add(name, el('span', undefined, 'THE'), el('span', undefined, 'BACKROOMS'));

    this.startBtn = button('ENTER', () => dispatch({ type: 'start', seed: this.readSeed() }), 'ui-btn ui-btn-primary');
    this.continueBtn = button('CONTINUE', () => dispatch({ type: 'continue' }), 'ui-btn ui-btn-primary');
    add(this.continueWrap, this.continueBtn, this.continueLine);
    this.setResume(null);

    this.seed = el('input', 'ui-seed');
    this.seed.type = 'text';
    this.seed.placeholder = 'seed';
    this.seed.spellcheck = false;
    this.seed.maxLength = 24;
    this.seed.setAttribute('aria-label', 'Optional seed');

    const seedRow = el('label', 'ui-seed-row');
    add(seedRow, el('span', 'ui-seed-key', 'SEED'), this.seed);

    const menu = el('nav', 'ui-title-menu');
    add(menu, this.continueWrap, this.startBtn, button('SETTINGS', openSettings), seedRow);

    add(
      this.el,
      el('div', 'ui-scanlines'),
      name,
      this.flavour,
      menu,
      this.record,
      el('p', 'ui-title-hint', 'WASD move · SHIFT run · CTRL crouch · SPACE jump · E interact · F light · M map · TAB items · ESC pause'),
    );
  }

  enter(): void {
    // Cosmetic only: which line greets you is allowed to be genuinely random.
    this.flavour.textContent = FLAVOUR[Math.floor(Math.random() * FLAVOUR.length)]!;
    const p = readProgress();
    this.record.textContent =
      p.runs === 0
        ? 'NO RECORD'
        : `RUNS ${p.runs} · LOST ${p.deaths} · DEEPEST LEVEL ${p.deepestLevel} · LONGEST ${fmtTime(p.bestTime)}`;
    this.focusDefault();
  }

  /**
   * Show or withdraw the offer. Called after `show('title')`, which focuses on
   * the way in — so this refocuses, and the order is the rule rather than a
   * detail. Enter on a focused button is handled by the browser before
   * `ui/ui.ts` ever sees the key, and the reflex press that follows a reload
   * would otherwise start a new run over the top of the saved one.
   */
  setResume(offer: ResumeOffer | null): void {
    this.resumable = offer !== null;
    this.continueWrap.hidden = !this.resumable;
    if (offer) this.continueLine.textContent = describeResume(offer);
    if (!this.el.hidden) this.focusDefault();
  }

  private focusDefault(): void {
    const target = this.resumable ? this.continueBtn : this.startBtn;
    target.focus({ preventScroll: true });
  }

  key(e: KeyboardEvent): boolean {
    if (e.code === 'Enter' || e.code === 'NumpadEnter') {
      // Whatever the focus is on. With a run waiting that is CONTINUE; with the
      // seed field focused it is still a new maze, which is what typing a seed
      // and pressing Enter has always meant.
      (this.resumable && document.activeElement === this.continueBtn ? this.continueBtn : this.startBtn).click();
      return true;
    }
    return false;
  }

  /** Blank means "surprise me", a number is used as-is, anything else is hashed. */
  private readSeed(): number | undefined {
    const text = this.seed.value.trim();
    if (!text) return undefined;
    if (/^[0-9]+$/.test(text)) return Number(text) >>> 0;
    return hashSeed(text);
  }
}

/** `LEVEL 2 — THE PIPES · 4 MINUTES AGO`, in the register of the record line. */
function describeResume(offer: ResumeOffer): string {
  const place = offer.place.toUpperCase();
  const ago = sinceText(Date.now() - offer.savedAt);
  return ago ? `${place} · ${ago}` : place;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * How long ago, roughly, and never more precisely than it is worth.
 *
 * Empty when the clock says the run was saved in the future, which means the
 * machine's time moved rather than that anything is wrong with the run. Written
 * when the title is shown rather than ticked while it sits open — nobody reads
 * a title screen for a minute and then checks whether the clause updated.
 */
function sinceText(ms: number): string {
  if (!Number.isFinite(ms) || ms < -MINUTE) return '';
  if (ms < MINUTE) return 'MOMENTS AGO';
  if (ms < HOUR) return plural(Math.round(ms / MINUTE), 'MINUTE');
  if (ms < DAY) return plural(Math.round(ms / HOUR), 'HOUR');
  return plural(Math.round(ms / DAY), 'DAY');
}

function plural(n: number, unit: string): string {
  return `${n} ${unit}${n === 1 ? '' : 'S'} AGO`;
}

/* ------------------------------------------------------------------ *
 * Pause
 * ------------------------------------------------------------------ */

export class PausedScreen implements Screen {
  readonly el = el('section', 'ui-screen ui-paused');
  private readonly resumeBtn: HTMLButtonElement;
  private readonly armed: ArmedButton[] = [];

  constructor(dispatch: Dispatch, openSettings: () => void, openJournal: () => void) {
    this.resumeBtn = button('RESUME', () => dispatch({ type: 'resume' }), 'ui-btn ui-btn-primary');
    const restart = armedButton('RESTART', 'RESTART — CONFIRM', () => dispatch({ type: 'restart' }));
    const quit = armedButton('QUIT TO TITLE', 'QUIT — CONFIRM', () => dispatch({ type: 'quit' }));
    this.armed.push(restart, quit);

    const menu = el('nav', 'ui-menu');
    add(
      menu,
      this.resumeBtn,
      button('SETTINGS', openSettings),
      button('FIELD NOTES', openJournal),
      restart.el,
      quit.el,
    );

    add(
      this.el,
      el('div', 'ui-scanlines'),
      el('h2', 'ui-menu-title', 'PAUSED'),
      el('p', 'ui-menu-sub', 'The hum does not stop while you are away.'),
      menu,
    );
  }

  enter(): void {
    for (const a of this.armed) a.reset();
    this.resumeBtn.focus({ preventScroll: true });
  }

  exit(): void {
    for (const a of this.armed) a.reset();
  }
}

/* ------------------------------------------------------------------ *
 * Settings
 * ------------------------------------------------------------------ */

export class SettingsScreen implements Screen {
  readonly el = el('section', 'ui-screen ui-settings');

  private settings: GameSettings;
  private readonly sync: Array<() => void> = [];
  private readonly backBtn: HTMLButtonElement;

  constructor(initial: GameSettings, private readonly onChange: (s: GameSettings) => void, onBack: () => void) {
    this.settings = cloneSettings(initial);

    const rows = el('div', 'ui-rows');
    add(
      rows,
      this.groupLabel('AUDIO'),
      this.range('MASTER VOLUME', 0, 1, 0.02, () => this.settings.masterVolume, (v) => (this.settings.masterVolume = v), (v) => `${Math.round(v * 100)}%`),
      this.toggle('MUTE', () => this.settings.muted, (v) => (this.settings.muted = v)),

      this.groupLabel('LOOK'),
      this.range('SENSITIVITY', 0.0005, 0.006, 0.0001, () => this.settings.sensitivity, (v) => (this.settings.sensitivity = v), (v) => (v * 1000).toFixed(1)),
      this.toggle('INVERT Y', () => this.settings.invertY, (v) => (this.settings.invertY = v)),
      this.range('FIELD OF VIEW', 60, 110, 1, () => this.settings.render.fov, (v) => (this.settings.render.fov = v), (v) => `${Math.round(v)}°`),

      this.groupLabel('DISPLAY'),
      this.choice<RenderSettings['quality']>('QUALITY', ['low', 'medium', 'high'], () => this.settings.render.quality, (v) => (this.settings.render.quality = v)),
      this.toggle('POST-FX', () => this.settings.render.postFx, (v) => (this.settings.render.postFx = v)),
      this.toggle('HEAD BOB', () => this.settings.render.headBob, (v) => (this.settings.render.headBob = v)),
      this.toggle('SUBTITLES', () => this.settings.subtitles, (v) => (this.settings.subtitles = v)),
    );

    this.backBtn = button('BACK', onBack, 'ui-btn ui-btn-primary');
    const menu = el('nav', 'ui-menu');
    add(menu, this.backBtn);

    add(
      this.el,
      el('div', 'ui-scanlines'),
      el('h2', 'ui-menu-title', 'SETTINGS'),
      rows,
      el('p', 'ui-menu-sub', 'Changes apply immediately and are saved.'),
      menu,
    );
    this.refresh();
  }

  enter(): void {
    this.backBtn.focus({ preventScroll: true });
  }

  setSettings(s: GameSettings): void {
    this.settings = cloneSettings(s);
    this.refresh();
  }

  private refresh(): void {
    for (const f of this.sync) f();
  }

  private emit(): void {
    this.onChange(cloneSettings(this.settings));
  }

  private groupLabel(text: string): HTMLElement {
    return el('div', 'ui-rows-group', text);
  }

  private range(
    label: string,
    min: number,
    max: number,
    step: number,
    get: () => number,
    set: (v: number) => void,
    format: (v: number) => string,
  ): HTMLElement {
    const input = el('input', 'ui-range');
    input.type = 'range';
    input.min = String(min);
    input.max = String(max);
    input.step = String(step);
    input.setAttribute('aria-label', label);
    const value = el('span', 'ui-row-value');
    input.addEventListener('input', () => {
      set(Number(input.value));
      value.textContent = format(get());
      this.emit();
    });
    this.sync.push(() => {
      input.value = String(get());
      value.textContent = format(get());
    });
    return row(label, input, value);
  }

  private toggle(label: string, get: () => boolean, set: (v: boolean) => void): HTMLElement {
    const b = button('', () => {
      set(!get());
      this.refresh();
      this.emit();
    }, 'ui-toggle');
    b.setAttribute('aria-label', label);
    this.sync.push(() => {
      const on = get();
      b.textContent = on ? 'ON' : 'OFF';
      b.classList.toggle('on', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    return row(label, b);
  }

  private choice<T extends string>(label: string, options: readonly T[], get: () => T, set: (v: T) => void): HTMLElement {
    const group = el('div', 'ui-choice');
    group.setAttribute('role', 'group');
    group.setAttribute('aria-label', label);
    const buttons = options.map((opt) => {
      const b = button(opt.toUpperCase(), () => {
        set(opt);
        this.refresh();
        this.emit();
      }, 'ui-choice-btn');
      group.appendChild(b);
      return b;
    });
    this.sync.push(() => {
      const cur = get();
      for (let i = 0; i < buttons.length; i++) {
        const on = options[i] === cur;
        buttons[i]!.classList.toggle('sel', on);
        buttons[i]!.setAttribute('aria-pressed', on ? 'true' : 'false');
      }
    });
    return row(label, group);
  }
}

/* ------------------------------------------------------------------ *
 * Death
 * ------------------------------------------------------------------ */

/** Phrasing for the causes the game is likely to hand us. */
const CAUSE_LINES: ReadonlyArray<readonly [RegExp, string]> = [
  [/hound/i, 'Something fast found you.'],
  [/smiler/i, 'You looked at it for too long.'],
  [/stealer|stalk/i, 'It had been walking behind you for a while.'],
  [/wretch/i, 'It got hold of you in a corridor.'],
  [/clump/i, 'You touched the wrong wall.'],
  [/fall/i, 'The floor was not where you left it.'],
  // The only cause that is another person. Deliberately blunt: whoever did it
  // stood in front of you for most of a second first, and they know it too.
  [/^player$/i, 'Someone you could see did that.'],
];

export class DeadScreen implements Screen {
  readonly el = el('section', 'ui-screen ui-dead');

  private readonly cause = el('p', 'ui-dead-cause');
  private readonly survived = el('span', 'ui-stat-value');
  private readonly depth = el('span', 'ui-stat-value');
  private readonly againBtn: HTMLButtonElement;

  constructor(dispatch: Dispatch) {
    this.againBtn = button('AGAIN', () => dispatch({ type: 'restart' }), 'ui-btn ui-btn-primary');
    const menu = el('nav', 'ui-menu');
    add(menu, this.againBtn, button('TITLE', () => dispatch({ type: 'quit' })));

    const stats = el('div', 'ui-dead-stats');
    add(stats, statRow('SURVIVED', this.survived), statRow('REACHED', this.depth));

    add(
      this.el,
      el('div', 'ui-scanlines'),
      el('p', 'ui-dead-eyebrow', 'END OF RUN'),
      el('h2', 'ui-dead-title', 'THE HUM CONTINUES'),
      this.cause,
      stats,
      menu,
    );
  }

  enter(): void {
    // Late, so the fade lands before anything is clickable.
    window.setTimeout(() => {
      if (!this.el.hidden) this.againBtn.focus({ preventScroll: true });
    }, 2200);
  }

  setResult(reason: string, survived: number, depth: number, levelName: string): void {
    const match = CAUSE_LINES.find(([re]) => re.test(reason));
    this.cause.textContent = match ? match[1] : reason || 'You stopped.';
    this.survived.textContent = fmtTime(survived);
    this.depth.textContent = levelName || `Level ${depth}`;
  }
}

/* ------------------------------------------------------------------ *
 * Shared bits
 * ------------------------------------------------------------------ */

function row(label: string, control: HTMLElement, value?: HTMLElement): HTMLElement {
  const r = el('div', 'ui-row');
  add(r, el('span', 'ui-row-label', label), control, value ?? el('span', 'ui-row-value'));
  return r;
}

function statRow(label: string, value: HTMLElement): HTMLElement {
  const r = el('div', 'ui-stat');
  add(r, el('span', 'ui-stat-key', label), value);
  return r;
}

interface ArmedButton {
  el: HTMLButtonElement;
  reset(): void;
}

/** Destructive actions need a second click; a misclick should not end a run. */
function armedButton(label: string, confirmLabel: string, onConfirm: () => void): ArmedButton {
  let armed = false;
  const b = button(label, () => {
    if (armed) {
      onConfirm();
      return;
    }
    armed = true;
    b.textContent = confirmLabel;
    b.classList.add('armed');
  });
  return {
    el: b,
    reset(): void {
      armed = false;
      b.textContent = label;
      b.classList.remove('armed');
    },
  };
}
